/**
 * 创建 / 编辑管理员弹窗（只有超管能打开）。编辑时用户名不可改，不显示密码和角色。
 */
import { App, Form, Input, Modal, Select } from 'antd'
import type { AdminListItem } from '@/types/admin'
import { ROLE_OPTIONS } from './constants'
import { apiErrorMessage, useSaveAdmin, type AdminFormValues } from './hooks'

interface AdminFormModalProps {
  open: boolean
  // 为 null 时创建
  admin: AdminListItem | null
  onClose: () => void
}

// 每次打开都重新挂载表单，关闭后状态不残留
export function AdminFormModal({ open, admin, onClose }: Readonly<AdminFormModalProps>) {
  if (!open) return null
  return <AdminForm key={admin?.id ?? 'new'} admin={admin} onClose={onClose} />
}

function AdminForm({ admin, onClose }: Readonly<Omit<AdminFormModalProps, 'open'>>) {
  const { message } = App.useApp()
  const [form] = Form.useForm<AdminFormValues>()
  const saveAdmin = useSaveAdmin()
  const isEdit = admin !== null

  const handleOk = async () => {
    let values: AdminFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    try {
      await saveAdmin.mutateAsync({ id: admin?.id, values })
      message.success(isEdit ? '管理员信息更新成功' : '管理员创建成功')
      onClose()
    } catch (error) {
      message.error(apiErrorMessage(error, '操作失败'))
    }
  }

  return (
    <Modal
      open
      title={isEdit ? '编辑管理员' : '创建管理员'}
      width={600}
      confirmLoading={saveAdmin.isPending}
      onOk={() => void handleOk()}
      onCancel={onClose}
    >
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        initialValues={{
          username: admin?.username ?? '',
          email: admin?.email ?? '',
          display_name: admin?.display_name ?? '',
          role: admin?.role
        }}
      >
        <Form.Item label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
          <Input disabled={isEdit} placeholder="请输入用户名" />
        </Form.Item>
        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            { required: true, message: '请输入邮箱' },
            { type: 'email', message: '请输入有效的邮箱' }
          ]}
        >
          <Input placeholder="请输入邮箱" />
        </Form.Item>
        {!isEdit && (
          <Form.Item
            label="初始密码"
            name="password"
            rules={[
              { required: true, message: '请输入初始密码' },
              { min: 8, message: '密码至少8位' }
            ]}
          >
            <Input.Password placeholder="请输入至少8位密码" />
          </Form.Item>
        )}
        <Form.Item label="显示名称" name="display_name">
          <Input placeholder="请输入显示名称" />
        </Form.Item>
        {!isEdit && (
          <Form.Item label="角色" name="role" rules={[{ required: true, message: '请选择角色' }]}>
            <Select aria-label="角色" placeholder="请选择角色" options={ROLE_OPTIONS} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
